<template>
	<div class="card-v2 card-recent-messages">

		<header class="flex-split mb-8 lg-mb-16">
			<h2 class="m0 pr-16">Recent Messages</h2>
			<router-link v-if="!loading && items.length" :to="{ name: $routes.Messages.name }" class="show-md fs-12 ">View All Messages</router-link>
		</header>

		<!-- Skeleton loading -->
		<div v-if="loading">
			<div v-for="index in 3" :key="index" class="pt-16 pb-16">
				<div class="skeleton pt-16 mb-8" style="width:30%"></div>
				<div class="skeleton pt-16"></div>
			</div>
		</div>

		<!-- Messages -->
		<template v-else-if="items.length">
			<div
				v-for="(item, i) in items"
				:key="item.chat.id"
				:class="[
					'flex-top pt-16 pb-16 lg-pl-16 lg-pr-16 color-gray item cursor-pointer',
					{
						'border-top-07': i > 0,
						'trial-item': chatRequiresUpgrade(item),
					},
				]"
				@click="toThread(item, $event)"
			>
				<template v-if="chatRequiresUpgrade(item)">
					<div class="square-40 round mr-16 bg-studio flex-center">
						<svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" fill="none" viewBox="0 0 13 12"><path stroke="#ED2644" stroke-width="1.5" d="M6.34336 8.0501C8.28936 8.0501 9.86836 6.4719 9.86836 4.525C9.86836 2.5782 8.28936 1 6.34336 1C4.39636 1 2.81836 2.5782 2.81836 4.525C2.81836 6.4719 4.39636 8.0501 6.34336 8.0501Z" clip-rule="evenodd"/><path stroke="#ED2644" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M1 11.1347C1.541 10.197 2.32 9.41838 3.258 8.87698C4.196 8.33568 5.26 8.05068 6.343 8.05078C7.426 8.05078 8.49 8.33578 9.428 8.87708C10.366 9.41848 11.145 10.1971 11.686 11.1348"/></svg>
					</div>
					<div class="fs-12 pt-4 lh-14 grow no-overflow flex-split flex-top">
						<div class="pr-8 md-pr-32">
							<div class="fs-14 fw-700 color-violet">{{ item.job.event.name }}</div>
							<div class="fs-12 lh-14">The Studio for {{ item.job.event.name }} has sent you a message. Accept the job and <UpgradeButton class="button-inline fs-12 fw-500 color-blue" :job="item.job" @click.native.stop>upgrade your account</UpgradeButton> to read the message.</div>
						</div>
						<div class="pl-8 no-wrap op-6">{{ formatMessageTime(item.lastMessage.createdAt) }}</div>
					</div>
				</template>
				<template v-else>
					<ProfileAvatar :profile="item.chatUserProfile" size="40" class="mr-16 square-40" @click.native.stop/>
					<div class="fs-12 lh-14 grow no-overflow">
						<div class="flex-split mb-4">
							<router-link :to="{ name: $routes.Profile.name, params: { id: item.chatUserProfile.id }}" class="color-violet fw-500 name" @click.native.stop>{{ item.chatUserProfile.name }}</router-link>
							<div class="pl-8">{{ formatMessageTime(item.lastMessage.createdAt) }}</div>
						</div>
						<div class="flex-split">
							<div class="pr-8 md-pr-80 no-overflow">
								<div class="fs-14 fw-700 color-violet mb-8">{{ item.job.event.name }}</div>
								<div class="truncate">{{ stripTags(item.lastMessage.content.text) }}</div>
							</div>
							<div v-if="item.unreadMessagesCount" class="unread-messages-count">{{ item.unreadMessagesCount }}</div>
						</div>
					</div>
				</template>
			</div>

			<div class="hide-md pt-16 pl-4 border-top-07">
				<router-link :to="{ name: $routes.Messages.name }" class="fs-12">View All Messages</router-link>
			</div>
		</template>

		<div v-else class="null-message">
			<svg width="97" height="57" viewBox="0 0 97 57" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="81.434" height="24.038" rx="4" fill="#DBF2FF"/><path d="M8 26.865V24h6l-4.36 3.633A1 1 0 0 1 8 26.865Z" fill="#DBF2FF"/><path d="M89 54.865V52h-6l4.36 3.633a1 1 0 0 0 1.64-.768Z" fill="#EEDAFE"/><rect opacity=".2" x="28.453" y="7.849" width="35.321" height="3.434" rx="1.717" fill="#0EA8FE"/><rect opacity=".2" x="28.453" y="13.736" width="23.057" height="2.453" rx="1.226" fill="#0EA8FE"/><circle opacity=".2" cx="14.226" cy="12.264" r="7.849" fill="#0EA8FE"/><path clip-rule="evenodd" d="M14.19 13.07a2.435 2.435 0 1 0 0-4.87 2.435 2.435 0 0 0 0 4.87Z" stroke="#0EA8FE"/><path d="M10.5 15.2a4.258 4.258 0 0 1 3.69-2.13 4.263 4.263 0 0 1 3.691 2.13" stroke="#0EA8FE" stroke-linecap="round" stroke-linejoin="round"/><rect x="15.208" y="27.962" width="81.434" height="24.038" rx="4" fill="#EEDAFE"/><circle opacity=".2" cx="28.453" cy="40.226" r="7.849" fill="#8D05FB"/><path clip-rule="evenodd" d="M28.29 40.87a2.435 2.435 0 1 0 0-4.87 2.435 2.435 0 0 0 0 4.87Z" stroke="#8D05FB"/><path d="M24.6 43a4.257 4.257 0 0 1 3.69-2.13A4.264 4.264 0 0 1 31.982 43" stroke="#8D05FB" stroke-linecap="round" stroke-linejoin="round"/><rect opacity=".2" x="40.717" y="35.811" width="35.321" height="3.434" rx="1.717" fill="#8D05FB"/><rect opacity=".2" x="40.717" y="41.698" width="23.057" height="2.453" rx="1.226" fill="#8D05FB"/></svg>
			<strong>No messages</strong>
			<p>You don't have any messages yet.</p>
		</div>
	</div>	
</template>

<script>
import { fetchMessages } from '@/services/swm/api';
import { formatMessageTime } from '@/helpers/format';
import { chatRequiresUpgrade } from '@/helpers/flags';

export default {
	name: 'CardRecentMessages',
	data() {
		return {
			loading: true,
			pageSize: 5,
			today: new Date().setHours(0, 0, 0, 0),
		};
	},
	methods: {
		formatMessageTime,
		chatRequiresUpgrade,
		async fetchData() {
			if (!this.items.length) {
				this.loading = true;
			}

			try {
				const messages = await fetchMessages({limit: 5});
				// TODO: should this filtering logic be moved inside the request?
				const filteredMessages = messages.filter((item) => item && item.chat && item.chatUserProfile && item.job?.event);
				this.items = filteredMessages;
				this.loading = false;
			} catch (error) {
				console.log('@@ fetchMessages error', error);
				this.loading = false;
			}
		},
		toThread(item) {
			if (this.chatRequiresUpgrade(item)) {
				return;
			}

			const params = {
				chatId: item.chat.id,
				jobId: item.job.id
			};

			this.$router.push({name: 'thread', params});
		},
		stripTags(html) {
			const div = document.createElement('div');
			div.innerHTML = html;

			return div.innerText;
		},
		async upgrade(jobId) {
			const store = this.$store;
			store.commit('onboarding/setIsUpgrading', true);
			store.commit('onboarding/setPostOnboardingRoute', { name: this.$routes.JobDetails.name, params: { jobId } });
			this.$emit('click');
			const path = store.getters['onboarding/currentPath'];
			await this.$router.push({ name: path.steps[0].routeName });
		},
	},
	computed: {
		items: {
			get() {
				return this.$store.getters['cache/dashboardMessages'];
			},
			set(items) {
				return this.$store.commit('cache/setCache', { name: 'dashboardMessages', items });
			},
		},
		freeJob() {
			return this.$store.getters['jobs/freeJobData'].freeJob;
		},
		subscription() {
			return this.$store.getters['subscriptions/current'];
		},
		isTrialing() {
			return !!this.subscription?.isTrialing;
		},
	},
	created() {
		this.fetchData();
	},
}
</script>
